import type { SalesViewSummary } from '../../api/types';
import { BAND_LABEL, BAND_ORDER } from './salesBandVocab';

interface SalesAgentWelcomeProps {
  /** True once the board has resolved role defaults and loaded a summary. */
  ready: boolean;
  summary: SalesViewSummary | null;
  totalClients: number;
  bandCounts: Record<string, number>;
  /** Name of the focused client, when the drawer is open. */
  selectedClientName?: string;
  busy: boolean;
  onPrompt: (prompt: string) => void;
}

/**
 * Mia's opening card on Sales View.
 *
 * Deliberately NOT the Client View card with different labels. That one is built out of dealers,
 * VIN priority buckets and inspection cadence; a CSE reading those learns the wrong thing about
 * what the assistant can see. The chips here are the two questions this surface actually answers,
 * kept apart on purpose: **who needs contact** (workflow bands) and **who is in the worst shape**
 * (tiers). Collapsing them into one list is the mistake §11.1 exists to prevent.
 */
// These chips are counted from the BOARD, which may be filtered to someone else's territory, so
// they ask about "my board" and never "my clients" -- the scope decides whose, not the wording.
const TIER_CHIPS: { key: keyof SalesViewSummary['tier_counts']; label: string; prompt: string }[] = [
  { key: 'urgent', label: 'Urgent', prompt: 'Which clients on my board are URGENT, and why did each one surface?' },
  { key: 'high', label: 'High', prompt: 'Show me the HIGH tier clients on my board and what is driving their scores' },
];

/* The week is the thing a rep actually leaves with, and nothing on this card pointed at it.
 * Phrased as the questions a rep asks out loud -- "what am I doing", "is this drive sensible",
 * "who got left out" -- rather than as tool names. */
const PLAN_CHIPS: { label: string; prompt: string }[] = [
  { label: 'My week', prompt: 'What does my week look like? Walk me through the visits and the calls.' },
  { label: 'My route', prompt: 'Show me the driving: each trip, its stops in order, and the miles between them.' },
  { label: 'Why these', prompt: 'Why did these clients make my week, and which ones were closest to the cut?' },
  { label: 'Who missed out', prompt: 'Which clients needed contact but did not fit in my week, and what would I drop to fit them?' },
];

export function SalesAgentWelcome({
  ready,
  summary,
  totalClients,
  bandCounts,
  selectedClientName,
  busy,
  onPrompt,
}: SalesAgentWelcomeProps) {
  // Only the bands that open expanded on the board, in the server's rank order. Nine chips would
  // be a list, not a starting point.
  const bands = BAND_ORDER.filter((key) => (bandCounts[key] ?? 0) > 0).slice(0, 4);

  return (
    <>
      <div className="agent-welcome-greeting">
        <span className="agent-welcome-mark">✦</span>
        <div>
          <div className="agent-welcome-title">Welcome to Mia</div>
          <div className="agent-welcome-sub">
            {!ready
              ? 'Standby while I pull your book…'
              : selectedClientName
                ? `Focused on ${selectedClientName} — here's where to start.`
                : `${totalClients.toLocaleString()} client${totalClients === 1 ? '' : 's'} in your current view — pick where you'd like to start.`}
          </div>
        </div>
      </div>

      {!ready && (
        <div className="agent-welcome-loading">
          <span className="agent-tool-spinner" aria-hidden="true" /> Gathering your book…
        </div>
      )}

      {ready && selectedClientName && (
        <div className="agent-welcome-section">
          <div className="agent-welcome-label">🏢 This client</div>
          <div className="agent-chip-wrap">
            <button className="agent-quick-chip" disabled={busy} onClick={() => onPrompt('Why did this client surface? Walk me through the signals.')}>
              Why they surfaced
            </button>
            <button className="agent-quick-chip" disabled={busy} onClick={() => onPrompt('How does this client compare to their territory peers?')}>
              Compare to peers
            </button>
            <button className="agent-quick-chip" disabled={busy} onClick={() => onPrompt('Give me the full rundown on this client and the one thing I should do next.')}>
              Full rundown
            </button>
          </div>
        </div>
      )}

      {ready && !selectedClientName && (
        <div className="agent-welcome-section">
          <div className="agent-welcome-label">🗓️ My week</div>
          <div className="agent-chip-wrap">
            {PLAN_CHIPS.map(({ label, prompt }) => (
              <button key={label} className="agent-quick-chip" disabled={busy} onClick={() => onPrompt(prompt)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {ready && !selectedClientName && bands.length > 0 && (
        <div className="agent-welcome-section">
          {/* Contact need, NOT tier. A client contacted yesterday is not this week's work
              whatever their tier, so this is the list a rep plans a week from. */}
          <div className="agent-welcome-label">📞 Who needs contact</div>
          <div className="agent-chip-wrap">
            {bands.map((key) => (
              <button
                key={key}
                className="agent-quick-chip"
                disabled={busy}
                onClick={() => onPrompt(`Which clients on my board are in the "${BAND_LABEL[key] ?? key}" band, ranked by contact need?`)}
              >
                {BAND_LABEL[key] ?? key}
                <span className="agent-quick-chip-count">{bandCounts[key]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {ready && !selectedClientName && summary && (
        <div className="agent-welcome-section">
          <div className="agent-welcome-label">⚠️ Worst shape</div>
          <div className="agent-chip-wrap">
            {TIER_CHIPS.filter(({ key }) => (summary.tier_counts[key] ?? 0) > 0).map(({ key, label, prompt }) => (
              <button key={key} className="agent-quick-chip" disabled={busy} onClick={() => onPrompt(prompt)}>
                {label}
                <span className="agent-quick-chip-count">{summary.tier_counts[key]}</span>
              </button>
            ))}
            {summary.went_dark_count > 0 && (
              <button
                className="agent-quick-chip"
                disabled={busy}
                onClick={() => onPrompt('Which clients on my board went dark, and what were they doing before they stopped?')}
              >
                Went dark
                <span className="agent-quick-chip-count">{summary.went_dark_count}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {ready && <div className="agent-welcome-hint">Or ask me anything below.</div>}
    </>
  );
}
